// Dynamic-RBAC Phase 2 (admin-portal). Reads the code-derived permission catalog
// and manages platform role definitions. All routes are platform-gated server-side
// and reached through the gateway → auth-service. Matches the app-wide axios client
// convention (VITE_API_URL, cookie session via withCredentials set globally).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

// A data scope a permission (or role) may imply — RBAC Model A.
export type DataScope = 'self' | 'direct_reports' | 'team' | 'tenant' | 'platform';

export const SCOPE_OPTIONS: { value: DataScope; label: string }[] = [
  { value: 'self', label: 'Self — only the person’s own records' },
  { value: 'direct_reports', label: 'Direct reports — immediate reports only (recommended for managers/executives)' },
  { value: 'team', label: 'Team — their ENTIRE reporting tree (every level down)' },
  { value: 'tenant', label: 'Tenant — everyone in the organization (reserve for HR/finance/audit)' },
  { value: 'platform', label: 'Platform — across all tenants' },
];

// One code-enforced permission string, e.g. "payroll:approve".
export interface CatalogPermission {
  key: string;
  description?: string;
  dataScope?: DataScope | string;
}

// Catalog is grouped by owning service.
export interface CatalogService {
  service: string;
  permissions: CatalogPermission[];
}

export interface PermissionCatalog {
  services: CatalogService[];
}

// A named bundle of permissions.
export interface PlatformRole {
  id: string;
  key: string;
  label: string;
  description?: string;
  isSystem: boolean;
  scope: DataScope | string;
  permissions: string[];
}

export interface CreateRoleBody {
  key: string;
  label: string;
  description?: string;
  scope: DataScope | string;
  permissions: string[];
}

// System roles are read-only server-side, so a PATCH only carries the editable bits.
export interface UpdateRoleBody {
  label?: string;
  description?: string;
  permissions?: string[];
}

export const getCatalog = () =>
  axios.get<PermissionCatalog>(`${API_V1}/platform/rbac/catalog`).then((r) => r.data);

export const listRoles = () =>
  axios.get<PlatformRole[]>(`${API_V1}/platform/rbac/roles`).then((r) => r.data);

export const createRole = (body: CreateRoleBody) =>
  axios.post<PlatformRole>(`${API_V1}/platform/rbac/roles`, body).then((r) => r.data);

export const updateRole = (id: string, body: UpdateRoleBody) =>
  axios.patch<PlatformRole>(`${API_V1}/platform/rbac/roles/${id}`, body).then((r) => r.data);

export const deleteRole = (id: string) =>
  axios.delete(`${API_V1}/platform/rbac/roles/${id}`).then((r) => r.data);

// Consistent error-surfacing: prefer the backend message (e.g. the 403
// "cannot grant a permission you do not hold" / system-role guardrails), then fall back.
export const rbacErrorMessage = (e: any, fallback: string): string =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback;

// Group a flat catalog into a { service -> permissions } lookup and a sorted service list.
export const flattenCatalog = (catalog: PermissionCatalog | null) => {
  const byService = new Map<string, CatalogPermission[]>();
  const byKey = new Map<string, CatalogPermission & { service: string }>();
  (catalog?.services || []).forEach((s) => {
    byService.set(s.service, s.permissions || []);
    (s.permissions || []).forEach((p) => byKey.set(p.key, { ...p, service: s.service }));
  });
  return { byService, byKey };
};
